<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="450px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        x-small
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Company Banks"
      text="Update company bank"
      class="mb-0"
      :loading="loading"
    >
      <form v-if="dialog && !loading">
        <v-container
          class="overflow"
          data-simplebar
        >
          <v-row no-gutters>
            <v-col cols="12">
              <v-autocomplete
                v-model="localCompanyBank.payroll_bank_id"
                v-validate="'required'"
                :items="payrollBanks"
                :error-messages="errors.collect('payroll_bank_id')"
                data-vv-name="payroll_bank_id"
                data-vv-as="bank" 
                item-text="name"
                item-value="id"
                value="id"
                label="Company Bank"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="localCompanyBank.bank_account_no"
                v-validate="bankValidate(localCompanyBank.payroll_bank_id)"
                :error-messages="errors.collect('bank_account_no')"
                data-vv-name="bank_account_no"
                :data-vv-as="localCompanyBank.payroll_bank_id === 19?'originator account no.' :'bank account no.'" 
                :label="localCompanyBank.payroll_bank_id === 19? 'Originator Account No':'Account No.'"
              />
            </v-col>

            <v-col
              v-show="localCompanyBank.payroll_bank_id === 25"
              cols="12"
            >
              <v-text-field
                v-model="localCompanyBank.corporate_id"
                v-validate="'length:6'"
                :error-messages="errors.collect('corporate_id')"
                data-vv-name="corporate_id"
                data-vv-as="corporate ID"
                label="REFLEX Corporate ID"
                type="number"
                @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>

            <v-col
              v-show="localCompanyBank.payroll_bank_id === 19"
              cols="12"
            >
              <v-text-field
                v-model="localCompanyBank.company_registered_id"
                v-validate="companyRegisteredIdValidate(localCompanyBank.payroll_bank_id)"
                label="Company ID in Maybank Autocredit System (MAS)"
                :error-messages="errors.collect('company_register_id')"
                data-vv-name="company_register_id"
                data-vv-as="company ID in MAS"
              />
            </v-col>

            <v-col
              v-show="localCompanyBank.payroll_bank_id === 19 || localCompanyBank.payroll_bank_id === 5"
              cols="12"
            >
              <v-text-field
                v-model="localCompanyBank.originator_id"
                v-validate="originatorIdValidate(localCompanyBank.payroll_bank_id)"
                :label="localCompanyBank.payroll_bank_id === 5?'Organisation Code' :'Originator ID'"
                :error-messages="errors.collect('originator_id')"
                data-vv-name="originator_id"
                :data-vv-as="localCompanyBank.payroll_bank_id === 5?'organisation code' :'originator id'"
              />
            </v-col>

            <v-col
              v-show="localCompanyBank.payroll_bank_id === 19"
              cols="12"
            >
              <v-text-field
                v-model="localCompanyBank.short_name"
                v-validate="shortNameValidate(localCompanyBank.payroll_bank_id)"
                label="Originator Short Name"
                :error-messages="errors.collect('short_name')"
                data-vv-name="short_name"
                data-vv-as="originator short name"
              />
            </v-col>

            <v-col cols="12">
              <span> Is Default? </span>
              <v-radio-group
                v-model="localCompanyBank.is_default"
                v-validate="'required'"
                :error-messages="errors.collect('is_default')"
                data-vv-name="is_default"        
                data-vv-as="is default"        
                row
              >
                <v-radio
                  label="No"
                  :value="0"
                />
                <v-radio
                  label="Yes"
                  :value="1"
                /> 
              </v-radio-group>
            </v-col>
          </v-row>
        </v-container>
      </form> 

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"      
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions> 
    </material-card>  
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      companyBank: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        dialog: null,
        progressing: false,
        payrollBanks: [],
        localCompanyBank: null,
        loading: false,
      }
    },

    watch: {
      dialog: function (value) {
        if (!value) return
        this.localCompanyBank = { ...this.companyBank }
        
        this.loading = true

        this.$api.payrollBanks().list()
          .then(res => {
            this.payrollBanks = res.data.filter(data => {
              return [19, 5, 25, 23].includes(data.id)
            })

            this.loading = false
          })
      },
    },
    methods: { 
      bankValidate (id) {
        if (!id) return 'required'
        if (id === 19) return 'required|max:12' // maybank
        return 'required'
      },

      originatorIdValidate (id) {
        if (!id) return ''
        if (id === 19) return 'required|max:5' // maybank
        if (id === 5) return 'required|max:10' // ambb
        return ''
      },

      companyRegisteredIdValidate (id) {
        if (!id) return ''
        if (id === 19) return 'required|max:3' // maybank
        return ''
      },

      shortNameValidate (id) {
        if (!id) return ''
        if (id === 19) return 'required|max:13' // maybank
        return ''
      },

      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.companyBanks().update(this.localCompanyBank.id, this.prepareFormData())
              .then(response => {
                this.$emit('updated')
                this.$snack({ message: response.data.status })

                this.progressing = false
                this.dialog = false
              })
              .catch((err) => {
                if (err.response.status === 405) {              
                  this.$snack({ 
                    color: 'error',
                    message: err.response.data.message, 
                  })
                }            
            
                this.progressing = false
              })
          })
      },

      prepareFormData () {
        return {
          payroll_bank_id: this.localCompanyBank.payroll_bank_id,
          bank_account_no: this.localCompanyBank.bank_account_no,
          is_default: this.localCompanyBank.is_default,
          originator_id: this.localCompanyBank.originator_id,
          short_name: this.localCompanyBank.short_name,
          company_registered_id: this.localCompanyBank.company_registered_id,
          corporate_id: this.localCompanyBank.corporate_id,
        }
      },

    },
  }
</script>
